"use client";

import { Box, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import { useState } from "react";

const pageFont = '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

type MarqueeLogo = {
  src: string;
  alt: string;
  size: string;
  /** Optical vertical nudge in px (positive = down). Does not change strip padding. */
  offsetY?: number;
};

const marqueeLogos: MarqueeLogo[] = [
  { src: "/images/brand-Malibu-logo.png", alt: "Malibu", size: "large" },
  {
    src: "https://storage.googleapis.com/cuo-website/KidSuper+Logo.png",
    alt: "KidSuper Studios",
    size: "xl",
  },
  { src: "/images/brand-RedBull.png", alt: "Red Bull", size: "default" },
  { src: "/images/brand-skrewball.png", alt: "Skrewball", size: "xxxl", offsetY: 10 },
  { src: "/images/brand-dr-squatch.png", alt: "Dr. Squatch", size: "default" },
  { src: "/images/brand-wilson.png", alt: "Wilson", size: "xl" },
  { src: "/images/brand-guayaki.png", alt: "Guayakí", size: "large" },
  { src: "/images/brand-shake-shack.png", alt: "Shake Shack", size: "xxxl" },
  { src: "/images/brand-Jameson-Logo.png", alt: "Jameson", size: "default" },
  { src: "/images/brand-topo_chico.png", alt: "Topo Chico", size: "default" },
  { src: "/images/brand-glenlivet.png", alt: "The Glenlivet", size: "default" },
  { src: "/images/brand-The-Nudge.png", alt: "The Nudge", size: "default" },
  { src: "/images/brand-EBBS.png", alt: "EBBS", size: "default" },
  { src: "/images/brand-long_drink.jpg", alt: "Long Drink", size: "default" },
  { src: "/images/brand-Montenegro.jpg", alt: "Montenegro", size: "default" },
  { src: "/images/brand-montauk_2.jpg", alt: "Montauk Brewing", size: "default" },
  { src: "/images/brand-denada_tequila.png", alt: "De Nada", size: "default" },
];

function getLogoHeight(size: string) {
  if (size === "xxxl") return { xs: 104, md: 120 };
  if (size === "xxl") return { xs: 88, md: 104 };
  if (size === "xl") return { xs: 72, md: 88 };
  if (size === "large") return { xs: 48, md: 56 };
  return { xs: 40, md: 44 };
}
function getLogoMaxWidth(size: string) {
  if (size === "xxxl") return 360;
  if (size === "xxl") return 320;
  if (size === "xl") return 280;
  if (size === "large") return 180;
  return 160;
}

const marqueeKeyframes = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-25%); }
`;

export default function RotatingLogoMarquee() {
  const [paused, setPaused] = useState(false);
  const duplicated = [...marqueeLogos, ...marqueeLogos, ...marqueeLogos, ...marqueeLogos];

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#fff",
        py: { xs: 2, md: 2.5 },
        px: { xs: 2, md: 4 },
        borderTop: "1px solid rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: { xs: 100, md: 130 },
      }}
    >
      <Typography
        sx={{
          fontFamily: pageFont,
          fontSize: { xs: "0.7rem", md: "0.75rem" },
          fontWeight: 600,
          letterSpacing: "0.2em",
          color: "rgba(0,0,0,0.55)",
          textTransform: "uppercase",
          textAlign: "center",
          mb: { xs: 1, md: 1.5 },
        }}
      >
        Trusted by Leading Brands
      </Typography>
      <Box
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        sx={{
          overflow: "hidden",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 5, md: 7 },
            width: "max-content",
            animation: `${marqueeKeyframes} 50s linear infinite`,
            animationPlayState: paused ? "paused" : "running",
          }}
        >
          {duplicated.map((logo, i) => (
            <Box
              key={`${logo.alt}-${i}`}
              component="img"
              src={logo.src}
              alt={logo.alt}
              sx={{
                height: getLogoHeight(logo.size),
                width: "auto",
                maxWidth: getLogoMaxWidth(logo.size),
                objectFit: "contain",
                flexShrink: 0,
                opacity: 0.9,
                transform: logo.offsetY ? `translateY(${logo.offsetY}px)` : undefined,
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
